<template>
	<div class="page">
		<Subscription
			v-if="comparePlansEnabled"
			@customer-portal="portalButtonClick"
			@session-refresh="refreshUserSession"
		/>
		<v-dialog
			v-model="portalRedirectDialog"
			:width="$vuetify.display.mobile ? '95%' : '500px'"
			persistent
		>
			<v-card class="portal-redirect-card sgnl-p-16">
				<v-card-text class="sgnl-flex sgnl-align-center sgnl-gap-12">
					<v-progress-circular />
					<span>Redirecting you to Subscription portal...</span>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { fetchAuthSession } from "aws-amplify/auth";
import * as Sentry from "@sentry/vue";
import { tiers, groupCode, AccountTierDetail, AccountGroupCode } from "@/js/tierconfig";
import Subscription from "./Subscription.vue";
import { postPortalSession } from "@/js/signaloidClient";
import { useUserStore } from "@/stores/user";

type ComponentData = {
	comparePlansEnabled: boolean;
	userCognitoGroups: string[];
	portalRedirectDialog: boolean;
};
export default defineComponent({
	components: { Subscription },
	name: "BillingPanel",
	data: (): ComponentData => ({
		comparePlansEnabled: true,
		userCognitoGroups: [],
		portalRedirectDialog: false,
	}),
	setup() {
		const userStore = useUserStore();
		return { userStore };
	},
	computed: {
		userSubscriptionTierCode(): "" | AccountGroupCode {
			return groupCode(this.userStore.currentUserSubscriptions?.Tier ?? "");
		},
		userSubscriptionTier(): "" | undefined | AccountTierDetail {
			if (this.userSubscriptionTierCode === "") {
				return "";
			}
			return tiers.find((e) => e.code === this.userSubscriptionTierCode);
		},
		userCanUseCustomerPortal(): boolean {
			return this.userCognitoGroups.includes("CustomerPortalUser");
		},
	},
	methods: {
		async postPortalSession() {
			try {
				const response = await postPortalSession();
				// console.log(response);
				const portalsessionURL = response.data["SessionURL"];
				// console.log('Billing Portal Session Id:', portalsessionURL);
				return portalsessionURL;
			} catch (error) {
				Sentry.captureException(new Error("Posting Customer Portal session failed"), {
					extra: { error: error },
				});
				console.log(error);
			}
		},
		portalButtonClick() {
			this.$posthog?.capture("stripe_portal_opened", {
				current_tier: this.userStore?.currentUserSubscriptions?.Tier,
			});
			this.portalRedirectDialog = true;
			this.postPortalSession()
				.then((sessionURL) => {
					window.location.href = sessionURL;
				})
				.catch((e) => {
					console.log(e);
					this.portalRedirectDialog = false;
				});
		},
		async refreshUserSession(session: Awaited<ReturnType<typeof fetchAuthSession>>) {
			const accessToken = session.tokens?.accessToken;
			this.userStore.registerCurrentUserTokens();
			await this.userStore.getCurrentUserSubscriptions();
			const groups = (accessToken?.payload?.["cognito:groups"] as string[]) ?? [];
			if (groups.length > 0) {
				this.userCognitoGroups = groups;
			}
		},
	},
	async mounted() {
		if (this.$route.hash !== "") {
			this.comparePlansEnabled = true;
		}
		try {
			const session = await fetchAuthSession();
			await this.refreshUserSession(session);
		} catch (error) {
			console.log(error);
			Sentry.captureException(new Error("Error getting fetchAuthSession()"), { extra: { error: error } });
		}
	},
});
</script>

<style lang="scss" scoped>
.portal-redirect-card {
	background: var(--sgnl-default-primary-background-mono);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	border-radius: var(--sgnl-border-radius-6);
}
</style>
